import React, { useState, useEffect, useCallback } from 'react';
import { X, FileCheck, AlertCircle, Eye, Loader2, Download, Video, CheckCircle2, Circle } from 'lucide-react';
import { DoctorDTO } from '@/services';
import { Button } from '../ui/button';
import { DocumentViewer } from '../common/DocumentViewer';
import { useAdminDoctorApproval } from '@/hooks/useAdminDoctorApproval';
import { toast } from 'sonner';
import { downloadDoctorProfilePdf } from '@/services/pdfExports';
import {
  DOC_TYPE_LABELS,
  buildDoctorReviewChecklist,
  formatDoctorCrmDisplay,
  normalizeAdminReviewDocument,
  partitionReviewDocuments,
} from '@/lib/adminDoctorReview';

interface DoctorDetailsModalProps {
  doctor: DoctorDTO | null;
  isOpen?: boolean;
  onClose?: () => void;
  /** Chamado após aprovação bem-sucedida no modal (pai só precisa atualizar a lista). */
  onApprove?: (doctorId: string) => void;
  /** Chamado após recusa bem-sucedida no modal (pai só precisa atualizar a lista). */
  onRejected?: (doctorId: string) => void;
  isApproving?: boolean;
  onViewDocument?: (doc: any) => void;
  /** Abre o formulário de recusa ao montar o modal (ex.: clique no X da tabela). */
  initialShowRejectionForm?: boolean;
}

function formatFileSize(size?: number | null): string {
  if (size === undefined || size === null || Number.isNaN(Number(size))) {
    return 'Tamanho indisponível';
  }
  const bytes = Number(size);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getDoctorStatusLabel(doctor: DoctorDTO): { label: string; className: string } {
  if (doctor.isApproved || doctor.status === 'APPROVED') {
    return {
      label: 'Aprovado',
      className: 'text-green-600 dark:text-green-400',
    };
  }
  if (doctor.status === 'REJECTED') {
    return {
      label: 'Recusado',
      className: 'text-red-600 dark:text-red-400',
    };
  }
  return {
    label: 'Pendente',
    className: 'text-yellow-600 dark:text-yellow-400',
  };
}

export const DoctorDetailsModal: React.FC<DoctorDetailsModalProps> = ({
  doctor,
  isOpen = false,
  onClose,
  onApprove,
  onRejected,
  isApproving = false,
  onViewDocument,
  initialShowRejectionForm = false,
}) => {
  const { getDoctorDocumentsForReview, approveDoctor, rejectDoctor, loading } = useAdminDoctorApproval();
  const [documents, setDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  
  // Document Viewer State
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>(undefined);

  const handleViewFile = async (doc: any) => {
    if (onViewDocument) {
      onViewDocument(doc);
      return;
    }
    // Fallback to internal DocumentViewer
    if (doc?.id) {
      setSelectedDocumentId(doc.id);
      setShowDocumentViewer(true);
    } else {
      setSelectedDocumentId(undefined);
      setShowDocumentViewer(true);
    }
  };

  const loadDocuments = useCallback(() => {
    if (!doctor) return;
    setDocsLoading(true);
    setDocsError(null);
    setDocuments([]);

    getDoctorDocumentsForReview(doctor.id)
      .then((data) => {
        setDocuments((data || []).map(normalizeAdminReviewDocument));
      })
      .catch((err) => {
        setDocsError(err?.message || 'Erro ao carregar documentos.');
        setDocuments([]);
      })
      .finally(() => {
        setDocsLoading(false);
      });
  }, [doctor, getDoctorDocumentsForReview]);

  useEffect(() => {
    if (isOpen && doctor) {
      setShowRejectionForm(initialShowRejectionForm);
      setRejectionReason('');
      loadDocuments();
    }
  }, [isOpen, doctor, initialShowRejectionForm, loadDocuments]);

  if (!isOpen || !doctor) return null;

  const statusInfo = getDoctorStatusLabel(doctor);
  const isRejected = doctor.status === 'REJECTED';
  const checklist = buildDoctorReviewChecklist(doctor, documents);
  const { papers, videos } = partitionReviewDocuments(documents);
  const crmDisplay = formatDoctorCrmDisplay(doctor.crm, doctor.crmState);

  const handleApprove = async () => {
    if (!checklist.canApprove) {
      toast.error(checklist.approveBlockReason || 'Requisitos de aprovação incompletos.');
      return;
    }
    const success = await approveDoctor(doctor.id);
    if (success) {
      if (onApprove) onApprove(doctor.id);
      if (onClose) onClose();
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Por favor, informe o motivo da recusa.');
      return;
    }
    const success = await rejectDoctor(doctor.id, rejectionReason);
    if (success) {
      if (onRejected) onRejected(doctor.id);
      if (onClose) onClose();
    }
  };

  const handleExportPdf = async () => {
    try {
      setIsExportingPdf(true);
      await downloadDoctorProfilePdf(doctor.id);
      toast.success('PDF do prontuário do médico gerado com sucesso.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar PDF do médico.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const renderDocCard = (doc: any) => {
    const isLegacy =
      doc.storageProvider !== 'CLOUDFLARE_R2' ||
      !doc.hasFile ||
      (doc.fileUrl && String(doc.fileUrl).includes('/opt/render/project/source'));
    const docType = doc.documentType || doc.type || '';
    const isVideo = docType === 'CONFIRMATION_VIDEO';

    return (
      <div
        key={doc.id}
        className="p-4 border border-gray-200 dark:border-neutral-800 rounded-lg bg-gray-50 dark:bg-neutral-800/50 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-teal-100 dark:bg-teal-900/30">
            {isVideo ? (
              <Video className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            ) : (
              <FileCheck className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            )}
          </div>
          <div>
            <h5 className="font-semibold text-sm text-gray-900 dark:text-white">
              {DOC_TYPE_LABELS[docType] || docType}{' '}
              ({doc.originalFileName || doc.fileName || 'Sem nome'})
              {doc.certificateKind ? ` • ${doc.certificateKind}` : ''}
            </h5>
            <p className="text-xs text-gray-500">
              Tamanho: {formatFileSize(doc.fileSize)} • Enviado em{' '}
              {new Date(doc.uploadedAt || doc.createdAt).toLocaleDateString('pt-BR')}
              {isLegacy && (
                <span className="ml-2 text-amber-600 dark:text-amber-400 font-semibold">
                  Legado — reenvio recomendado
                </span>
              )}
            </p>
            {doc.status && (
              <span
                className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-md mt-1 ${
                  doc.status === 'APPROVED'
                    ? 'bg-green-100 text-green-800'
                    : doc.status === 'REJECTED'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-amber-100 text-amber-800'
                }`}
              >
                {doc.status}
              </span>
            )}
            {doc.status === 'REJECTED' && doc.rejectionReason && (
              <p className="text-xs text-red-600 mt-1 italic">
                Motivo: {doc.rejectionReason}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewFile(doc)}
            className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-md transition-colors flex items-center gap-1 text-xs font-semibold cursor-pointer"
            title="Download/Visualizar"
          >
            <Eye className="w-4 h-4" />
            Visualizar
          </button>
        </div>
      </div>
    );
  };

  const ChecklistItem = ({ ok, label }: { ok: boolean; label: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle2 className="w-4 h-4 text-green-600" />
      ) : (
        <Circle className="w-4 h-4 text-gray-400" />
      )}
      <span className={ok ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500'}>{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/30 dark:to-blue-900/30 border-b border-teal-200 dark:border-teal-800 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Perfil do Médico
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportPdf}
              disabled={isExportingPdf}
              className="h-9 gap-2 border-teal-200 text-teal-700 hover:bg-teal-50"
            >
              {isExportingPdf ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isExportingPdf ? 'Gerando...' : 'Exportar PDF'}
            </Button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Doctor Info */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
              {(doctor.user?.name?.charAt(0) || 'D').toUpperCase()}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Dr. {doctor.user?.name || 'N/A'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {doctor.specialty}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                {doctor.user?.email || 'N/A'}
              </p>
            </div>
          </div>

          {/* CRM and Status */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-neutral-800 rounded-lg">
            <div>
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">CRM</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1 font-mono">
                {crmDisplay}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</p>
              <div className="flex items-center gap-1 mt-1">
                {statusInfo.label === 'Aprovado' ? (
                  <div className="w-3 h-3 bg-green-600 rounded-full" />
                ) : statusInfo.label === 'Recusado' ? (
                  <div className="w-3 h-3 bg-red-600 rounded-full" />
                ) : (
                  <div className="w-3 h-3 bg-yellow-600 rounded-full" />
                )}
                <span className={`text-lg font-semibold ${statusInfo.className}`}>
                  {statusInfo.label}
                </span>
              </div>
            </div>
          </div>

          {/* Checklist de análise */}
          <div className="p-4 border border-gray-200 dark:border-neutral-800 rounded-lg space-y-2">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
              Checklist de análise
            </p>
            <ChecklistItem ok={checklist.hasIdentityData} label="Dados do médico" />
            <ChecklistItem ok={checklist.hasCrm} label="CRM / UF" />
            <ChecklistItem ok={!!checklist.hasSpecialtyCertificate || checklist.isLegacyDocumentsOnly} label="Certificado de Especialidade" />
            <ChecklistItem ok={!!checklist.hasInscriptionProof || checklist.isLegacyDocumentsOnly} label="Comprovante de Inscrição" />
            <ChecklistItem
              ok={checklist.hasConfirmationVideo || checklist.isLegacyDocumentsOnly}
              label={
                checklist.isLegacyDocumentsOnly
                  ? 'Vídeo de confirmação (legado — não exigido)'
                  : 'Vídeo de confirmação'
              }
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
              Assinatura de receita/atestado/exame é na Memed (Bird ID). O .pfx no Dr House é
              opcional e não impede a aprovação.
            </p>
            {checklist.hasDigitalCertificate ? (
              <ChecklistItem
                ok
                label="Certificado .pfx no Dr House (opcional, já enviado)"
              />
            ) : null}
          </div>

          {/* Additional Info */}
          {doctor.biography && (
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
                Biografia
              </p>
              <p className="text-gray-700 dark:text-gray-300">{doctor.biography}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {doctor.consultationPrice && (
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  Preço da Consulta
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                  R$ {doctor.consultationPrice.toFixed(2)}
                </p>
              </div>
            )}
            {doctor.createdAt && (
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  Data de Candidatura
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                  {new Date(doctor.createdAt).toLocaleDateString('pt-BR')}
                </p>
              </div>
            )}
          </div>

          {/* Documents Section */}
          <div className="border-t border-gray-200 dark:border-neutral-800 pt-6">
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase mb-4">
              Documentos Profissionais
            </p>

            {docsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Carregando documentos...</span>
              </div>
            ) : docsError ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{docsError}</span>
                </div>
                <Button variant="outline" onClick={loadDocuments} className="h-9">
                  Tentar novamente
                </Button>
              </div>
            ) : documents.length > 0 ? (
              <div className="space-y-4">
                {papers.length > 0 && (
                  <div className="space-y-3">
                    {papers.map((doc) => renderDocCard(doc))}
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-3 flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Vídeo de confirmação
                  </p>
                  {videos.length > 0 ? (
                    <div className="space-y-3">{videos.map((doc) => renderDocCard(doc))}</div>
                  ) : (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200">
                      Nenhum vídeo de confirmação enviado.
                    </div>
                  )}
                </div>
              </div>
            ) : doctor.professionalDocumentsUrl ? (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <FileCheck className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-green-700 dark:text-green-300">
                        Documento Legado Enviado
                      </h4>
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        Cadastro anterior à exigência de vídeo. O documento profissional legado está disponível para análise.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewFile({})}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Visualizar
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-700 dark:text-amber-300">
                      Nenhum documento encontrado para este médico.
                    </h4>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!doctor.isApproved && !isRejected && (
            <div className="border-t border-gray-200 dark:border-neutral-800 pt-6 space-y-4">
              {!checklist.canApprove && checklist.approveBlockReason && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {checklist.approveBlockReason}
                  </p>
                </div>
              )}
              {showRejectionForm ? (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-gray-600 uppercase">Motivo da Recusa</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Escreva aqui o motivo detalhado para a recusa dos documentos..."
                    className="w-full h-24 p-3 border border-gray-300 dark:border-neutral-700 rounded-lg dark:bg-neutral-800 dark:text-white text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleReject}
                      disabled={loading || isApproving}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                    >
                      {loading ? 'Processando...' : 'Confirmar Recusa'}
                    </Button>
                    <Button
                      onClick={() => setShowRejectionForm(false)}
                      variant="ghost"
                      className="flex-1 text-gray-600"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Button
                    onClick={handleApprove}
                    disabled={loading || isApproving || !checklist.canApprove}
                    title={
                      !checklist.canApprove
                        ? checklist.approveBlockReason || 'Requisitos incompletos'
                        : 'Aprovar Médico'
                    }
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 animate-fade-in disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading || isApproving ? 'Aprovando...' : 'Aprovar Médico'}
                  </Button>
                  <Button
                    onClick={() => setShowRejectionForm(true)}
                    disabled={loading || isApproving}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 animate-fade-in"
                  >
                    Recusar Médico
                  </Button>
                  <Button
                    onClick={onClose}
                    className="flex-1 bg-gray-200 dark:bg-neutral-800 hover:bg-gray-300 dark:hover:bg-neutral-700 text-gray-900 dark:text-white font-semibold py-2"
                  >
                    Fechar
                  </Button>
                </div>
              )}
            </div>
          )}

          {doctor.isApproved && (
            <div className="border-t border-gray-200 dark:border-neutral-800 pt-6 text-center">
              <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
                <FileCheck className="w-6 h-6" />
                <p className="font-semibold">Médico já aprovado</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentViewer
        fileName="Documentos Profissionais"
        isOpen={showDocumentViewer}
        onClose={() => setShowDocumentViewer(false)}
        doctorId={doctor.id}
        documentId={selectedDocumentId}
      />
    </div>
  );
};
